import React, { useState, useEffect, useRef } from "react";
import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FiMessageCircle } from "react-icons/fi";
import { Button } from "newui/components/Button";
import {
  ClarificationModalContent,
  type ClarificationModalContentProps,
} from "../ClarificationModal/ClarificationModalContent";
import { ClarificationChatPanel } from "../ClarificationChat";
import { useFileUploadContext } from "newui/components/FileUpload/hooks/FileUploadContext";

interface ClarificationModalWithChatProps extends ClarificationModalContentProps {
  isOpen: boolean;
  rfxId: number;
  unreadCount?: number;
  onChatOpened?: () => void;
}

export const ClarificationModalWithChat: React.FC<ClarificationModalWithChatProps> = ({
  isOpen,
  rfxId,
  clarification,
  clarificationTypes,
  isClientView,
  supplierName,
  onChatOpened,
  onClientSave,
  onClose,
  onSupplierSave,
  unreadCount = 0,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(clarification?.is_sent);
  const hasAppliedDefaultRef = useRef(false);
  const { isSafeToClose } = useFileUploadContext();

  useEffect(() => {
    if (isOpen && clarification?.is_sent && !hasAppliedDefaultRef.current) {
      hasAppliedDefaultRef.current = true;
      setIsChatOpen(true);
      onChatOpened?.();
    }
    if (!isOpen) {
      hasAppliedDefaultRef.current = false;
    }
  }, [isOpen, clarification, onChatOpened]);

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const canChat = clarification?.is_sent === true;

  const handleClose = () => {
    if (!isSafeToClose()) {
      return;
    }
    setIsChatOpen(false);
    onClose();
  };

  const handleToggleChat = () => {

    if (!isSafeToClose()) {
      return;
    }

    setIsChatOpen((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        onChatOpened?.();
      }
      return willOpen;
    });
  };

  const closeChat = () => {
    if (!isSafeToClose()) {
      return;
    }
    setIsChatOpen(false);
  }

  const showUnreadOnToggle = !isChatOpen && unreadCount > 0;

  const chatToggleButton = canChat ? (
    <Button
      btntype="base"
      onClick={handleToggleChat}
      className="flex items-center gap-2 !px-3 !py-2"
      title={isChatOpen ? "Close discussion thread" : "Open discussion thread"}
    >
      <FiMessageCircle className="h-4 w-4" />
      {isChatOpen ? "Close Chat" : "Chat"}
      {showUnreadOnToggle && (
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </Button>
  ) : null;

  const getMaxWidth = () => {
    if (!isChatOpen) {
      return isMobile ? "95vw" : "800px";
    }
    return isMobile ? "95vw" : "1300px";
  };

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      motionPreset="scale"
      size="full"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="xl"
        bg="white"
        boxShadow="2xl"
        maxW={getMaxWidth()}
        maxH={isMobile ? "98vh" : "95vh"}
        mx="auto"
        display="flex"
        flexDirection={isMobile && isChatOpen ? "column" : "row"}
        overflow="hidden"
        transition="max-width 0.3s ease"
      >
        {/* Left panel — clarification content */}
        <div
          className="relative flex flex-col overflow-hidden"
          style={{
            minWidth: isMobile ? "100%" : "800px",
            maxWidth: isMobile ? "100%" : "800px",
            minHeight: isMobile && isChatOpen ? "50%" : undefined,
            flexShrink: 0,
          }}
        >
          <ModalCloseButton className="z-[999]" size="md" />
          <ClarificationModalContent
            clarification={clarification}
            clarificationTypes={clarificationTypes}
            isClientView={isClientView}
            supplierName={supplierName}
            onClientSave={onClientSave}
            onClose={handleClose}
            onSupplierSave={onSupplierSave}
            footerExtra={chatToggleButton}
          />
        </div>

        {/* Bottom/Right panel — chat */}
        {isChatOpen && canChat && clarification && (
          <div
            className="flex flex-col overflow-hidden border-gray-200"
            style={{
              width: isMobile ? "100%" : "500px",
              minWidth: isMobile ? "100%" : "500px",
              maxHeight: isMobile ? "50vh" : undefined,
              overflowY: isMobile ? "auto" : undefined,
              borderTopWidth: isMobile ? "1px" : "0",
              borderLeftWidth: isMobile ? "0" : "1px",
              borderStyle: "solid",
              borderColor: "#e2e8f0",
            }}
          >
            <ClarificationChatPanel
              clarification={clarification}
              hideThreadBar
              showThreadRef={false}
              isOpen={isChatOpen}
              rfxId={rfxId}
              onClose={closeChat}
            />
          </div>
        )}
      </ModalContent>
    </ChakraModal>
  );
};
